import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { 
  CalendarIcon, 
  CheckCircle2, 
  XCircle, 
  Save,
  Pill,
  Building2,
  User,
  PenLine,
  AlertTriangle,
  ScanLine,
  Plus,
  Trash2,
  Users,
  ShieldAlert,
  Stethoscope,
  MapPin,
  CreditCard,
  Calculator
} from 'lucide-react';
import QatarIDScanner from '../components/QatarIDScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_STORAGE_KEY = 'medx_new_patient_form';

const NewReceiptPage = () => {
  const { t, language, isRTL } = useLanguage();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const barcodeInputRef = useRef(null);
  
  // Load saved form data from sessionStorage
  const loadSavedForm = () => {
    try {
      const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore date as Date object
        if (parsed.receipt_date) {
          parsed.receipt_date = new Date(parsed.receipt_date);
        }
        return parsed;
      }
    } catch (e) {
      console.log('Failed to load saved form:', e);
    }
    return null;
  };

  const defaultFormData = {
    patient_name: '',
    patient_id_number: '',
    patient_age: '',
    receipt_date: new Date(),
    hospital_name: '',
    location_id: user?.location_id || '',
    receipt_status: 'received',
    reception_signature: user?.full_name || '',
    notify_hospital: false,
    receipt_type: 'self',
    has_allergy: false,
    allergy_notes: '',
    medical_notes: '',
    dose_mg: '',
    tablets: 0,
    family_members: [],
    prevented_reason: ''
  };

  const [formData, setFormData] = useState(() => loadSavedForm() || defaultFormData);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [alreadyReceivedWarning, setAlreadyReceivedWarning] = useState(null);
  const [checkingId, setCheckingId] = useState(false);
  const [barcodeMode, setBarcodeMode] = useState(false);
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [locations, setLocations] = useState([]);
  const [doseRules, setDoseRules] = useState([]);
  
  // Qatar ID Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  
  // Family member scanner state
  const [familyScannerOpen, setFamilyScannerOpen] = useState(false);
  const [scanningFamilyIndex, setScanningFamilyIndex] = useState(null);

  // Auto-save form data to sessionStorage
  useEffect(() => {
    const dataToSave = {
      ...formData,
      receipt_date: formData.receipt_date instanceof Date ? formData.receipt_date.toISOString() : formData.receipt_date
    };
    sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [formData]);

  // Clear saved data helper
  const clearSavedForm = () => {
    sessionStorage.removeItem(FORM_STORAGE_KEY);
  };

  // Handle scan complete - fill form fields
  const handleScanComplete = async (data) => {
    if (data.id_number) {
      setFormData(prev => ({
        ...prev,
        patient_id_number: data.id_number,
        patient_name: data.name || prev.patient_name,
        patient_age: data.age > 0 ? String(data.age) : prev.patient_age
      }));
      
      try {
        await axios.post(`${API}/audit/scan?scan_type=scanner&success=true&patient_id=${data.id_number}`);
      } catch (err) {
        console.log('Audit log failed:', err);
      }
      
      if (data.id_number) {
        checkPatientId(data.id_number);
      }
      
      toast.success(isRTL ? 'تم استخراج البيانات بنجاح' : 'Data extracted successfully');
    }
  };

  // Lookup patient by ID
  const lookupPatient = async (idNumber) => {
    try {
      const response = await axios.get(`${API}/patients/lookup/${idNumber}`);
      return response.data;
    } catch (err) {
      console.error('Lookup error:', err);
      return { found: false };
    }
  };

  // Fetch locations and dose rules
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, rulesRes] = await Promise.all([
          axios.get(`${API}/locations`),
          axios.get(`${API}/dose-rules`)
        ]);
        setLocations(locRes.data);
        setDoseRules(rulesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Auto-fill reception signature with logged-in user
  useEffect(() => {
    if (user?.full_name && !formData.reception_signature) {
      setFormData(prev => ({ ...prev, reception_signature: user.full_name }));
    }
  }, [user]);

  // Calculate tablets based on age
  const calculateTablets = useCallback((age) => {
    if (!age || age <= 0 || doseRules.length === 0) return 0;
    const rule = doseRules.find(r => age >= r.min_age && age <= r.max_age);
    return rule ? rule.tablets : 0;
  }, [doseRules]);

  // Auto-calculate tablets when age changes - REMOVED: tablets are now entered manually
  // The calculateTablets function is kept for reference display only

  // Barcode scanner support
  useEffect(() => {
    let buffer = '';
    let timeout = null;
    
    const handleKeyPress = (e) => {
      if (barcodeMode && barcodeInputRef.current === document.activeElement) {
        if (timeout) clearTimeout(timeout);
        if (e.key !== 'Enter') {
          buffer += e.key;
        }
        if (e.key === 'Enter' && buffer.length > 3) {
          e.preventDefault();
          processBarcode(buffer);
          buffer = '';
        }
        timeout = setTimeout(() => { buffer = ''; }, 100);
      }
    };
    
    window.addEventListener('keypress', handleKeyPress);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      if (timeout) clearTimeout(timeout);
    };
  }, [barcodeMode]);

  const processBarcode = async (barcode) => {
    const idNumber = barcode.trim();
    handleChange('patient_id_number', idNumber);
    
    try {
      const response = await axios.get(`${API}/patients/lookup/${idNumber}`);
      if (response.data.found) {
        handleChange('patient_name', response.data.patient_name);
        handleChange('has_allergy', response.data.has_allergy);
        handleChange('allergy_notes', response.data.allergy_notes || '');
        toast.success(isRTL ? 'تم العثور على بيانات المريض' : 'Patient data found');
      } else {
        toast.info(isRTL ? 'مريض جديد - يرجى إدخال البيانات' : 'New patient - please enter details');
      }
    } catch (error) {
      console.error('Barcode lookup error:', error);
    }
    
    checkPatientId(idNumber);
    setBarcodeMode(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Logic: If allergy = yes → patient dose = 0, patient tablets = 0
      // But do NOT force status to not_received if there are family members without allergy
      if (field === 'has_allergy' && value === true) {
        newData.dose_mg = 0;
        newData.tablets = 0;
        newData.prevented_reason = isRTL ? 'حساسية' : 'Allergy';
        // Only force not_received if receipt_type is 'self' (no family)
        if (newData.receipt_type === 'self') {
          newData.receipt_status = 'not_received';
        }
      }
      
      // Logic: If status != received → patient dose = 0
      if (field === 'receipt_status' && value !== 'received') {
        newData.dose_mg = 0;
        newData.tablets = 0;
      }
      
      if (field === 'patient_id_number') {
        setAlreadyReceivedWarning(null);
      }
      
      return newData;
    });
  };

  const checkPatientId = useCallback(async (idNumber) => {
    if (!idNumber || idNumber.length < 3) return;
    
    setCheckingId(true);
    try {
      const response = await axios.get(`${API}/receipts/check/${idNumber}`);
      if (response.data.already_received) {
        setAlreadyReceivedWarning(response.data);
      } else {
        setAlreadyReceivedWarning(null);
      }
    } catch (error) {
      console.error('Error checking patient ID:', error);
    } finally {
      setCheckingId(false);
    }
  }, []);

  // Family member functions
  const addFamilyMember = () => {
    setFormData(prev => ({
      ...prev,
      family_members: [
        ...prev.family_members,
        { name: '', id_number: '', age: '', has_allergy: false, allergy_notes: '', dose_mg: '', tablets: 0 }
      ]
    }));
  };

  const removeFamilyMember = (index) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.filter((_, i) => i !== index)
    }));
  };

  const updateFamilyMember = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.map((member, i) => {
        if (i === index) {
          const updated = { ...member, [field]: value };
          // If allergy → dose = 0, tablets = 0
          if (field === 'has_allergy' && value === true) {
            updated.dose_mg = 0;
            updated.tablets = 0;
          }
          return updated;
        }
        return member;
      })
    }));
  };

  // Family member QID scan
  const openFamilyScanner = (index) => {
    setScanningFamilyIndex(index);
    setFamilyScannerOpen(true);
  };

  const handleFamilyScanComplete = async (data) => {
    if (scanningFamilyIndex === null) return;
    const idx = scanningFamilyIndex;
    
    // Check for duplicate QID within family members
    if (data.id_number) {
      const duplicate = formData.family_members.find((m, i) => i !== idx && m.id_number === data.id_number);
      if (duplicate) {
        toast.error(isRTL ? 'رقم البطاقة مكرر في قائمة العائلة' : 'Duplicate QID in family list');
        setFamilyScannerOpen(false);
        setScanningFamilyIndex(null);
        return;
      }
      // Also check against main patient
      if (data.id_number === formData.patient_id_number) {
        toast.error(isRTL ? 'رقم البطاقة مطابق للمريض الرئيسي' : 'QID matches main patient');
        setFamilyScannerOpen(false);
        setScanningFamilyIndex(null);
        return;
      }
    }

    // Update the family member at the scanned index
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.map((member, i) => {
        if (i === idx) {
          return {
            ...member,
            id_number: data.id_number || member.id_number,
            name: data.name || member.name,
            age: data.age > 0 ? String(data.age) : member.age
          };
        }
        return member;
      })
    }));

    // Log scan for audit
    if (data.id_number) {
      try {
        await axios.post(`${API}/audit/scan?scan_type=scanner&success=true&patient_id=${data.id_number}`);
      } catch (err) { /* ignore */ }
    }

    toast.success(isRTL ? 'تم استخراج بيانات فرد العائلة' : 'Family member data extracted');
    setFamilyScannerOpen(false);
    setScanningFamilyIndex(null);
  };

  // Check duplicate QID when manually entering family member ID
  const checkFamilyDuplicateQID = (index, idNumber) => {
    if (!idNumber) return false;
    const dup = formData.family_members.find((m, i) => i !== index && m.id_number === idNumber);
    if (dup) {
      toast.error(isRTL ? 'رقم البطاقة مكرر في قائمة العائلة' : 'Duplicate QID in family list');
      return true;
    }
    if (idNumber === formData.patient_id_number) {
      toast.error(isRTL ? 'رقم البطاقة مطابق للمريض الرئيسي' : 'QID matches main patient');
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.patient_name || !formData.patient_id_number) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    // Age is required
    if (!formData.patient_age || parseInt(formData.patient_age) <= 0 || parseInt(formData.patient_age) > 150) {
      toast.error(isRTL ? 'يرجى إدخال عمر صحيح للمريض' : 'Please enter a valid patient age');
      return;
    }

    // Dose is REQUIRED only when: status = received AND no allergy (for self type)
    // For family type: patient dose can be 0 if patient has allergy, but family members need doses
    if (formData.receipt_status === 'received') {
      if (formData.receipt_type === 'self' && !formData.has_allergy) {
        if (!formData.dose_mg || parseFloat(formData.dose_mg) <= 0) {
          toast.error(isRTL ? 'يرجى إدخال عدد الحبات' : 'Please enter number of tablets');
          return;
        }
      }
    }

    // Location required for received status
    if (formData.receipt_status === 'received' && !formData.location_id) {
      toast.error(isRTL ? 'يرجى تحديد موقع التوزيع' : 'Please select distribution location');
      return;
    }
    
    // Medical notes required if allergy
    if (formData.has_allergy && !formData.medical_notes) {
      toast.error(isRTL ? 'الملاحظات الطبية مطلوبة في حالة الحساسية' : 'Medical notes required for allergy cases');
      return;
    }
    
    // Prevent submission if patient already received
    if (alreadyReceivedWarning) {
      toast.error(isRTL ? 'هذا المريض استلم الدواء مسبقاً!' : 'This patient has already received the medication!');
      return;
    }

    // Validate family members
    if (formData.receipt_type === 'family') {
      for (const member of formData.family_members) {
        if (!member.name || !member.id_number) {
          toast.error(isRTL ? 'يرجى إكمال بيانات جميع أفراد العائلة' : 'Please complete all family member details');
          return;
        }
        if (!member.age || parseInt(member.age) <= 0) {
          toast.error(isRTL ? 'يرجى إدخال عمر صحيح لجميع أفراد العائلة' : 'Please enter valid age for all family members');
          return;
        }
      }
    }
    
    setIsSubmitting(true);
    
    try {
      // Get location name for hospital_name field (backward compat)
      const selectedLocation = locations.find(l => l.id === formData.location_id);
      
      const payload = {
        ...formData,
        patient_age: parseInt(formData.patient_age) || 0,
        receipt_date: format(formData.receipt_date instanceof Date ? formData.receipt_date : new Date(formData.receipt_date), 'yyyy-MM-dd'),
        dose_mg: parseFloat(formData.dose_mg) || 0,
        tablets: parseInt(formData.tablets) || 0,
        hospital_name: selectedLocation?.name || formData.hospital_name || '',
        family_members: formData.family_members.map(m => ({
          ...m,
          age: parseInt(m.age) || 0,
          dose_mg: parseFloat(m.dose_mg) || 0,
          tablets: parseInt(m.tablets) || 0
        }))
      };
      
      await axios.post(`${API}/receipts`, payload);
      
      // Clear saved form on successful submission
      clearSavedForm();
      
      toast.success(t('receipt_created'));
      navigate('/receipts');
    } catch (error) {
      console.error('Error creating receipt:', error);
      const errorMsg = error.response?.data?.detail || (isRTL ? 'حدث خطأ أثناء التسجيل' : 'Error creating receipt');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    clearSavedForm();
    setFormData({
      ...defaultFormData,
      reception_signature: user?.full_name || '',
      location_id: user?.location_id || ''
    });
    setAlreadyReceivedWarning(null);
    toast.success(isRTL ? 'تم مسح النموذج' : 'Form cleared');
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="new-receipt-page">
      <div className="animate-fade-in flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {t('new_receipt')}
          </h1>
          <p className="text-slate-500">{t('intro_text')}</p>
        </div>
        <Button variant="outline" onClick={handleReset} className="gap-2 text-slate-500">
          {isRTL ? 'مسح النموذج' : 'Clear Form'}
        </Button>
      </div>

      {/* Qatar ID Scanner Section */}
      <Card className="animate-fade-in border-sky-200 bg-gradient-to-r from-sky-50 to-indigo-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <CreditCard className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">
                {isRTL ? 'مسح بطاقة قطر الشخصية' : 'Qatar ID Card Scan'}
              </h3>
              <p className="text-sm text-slate-500">
                {isRTL ? 'امسح البطاقة لملء البيانات تلقائياً' : 'Scan ID card to auto-fill data'}
              </p>
            </div>
            <Button 
              type="button"
              onClick={() => setScannerOpen(true)}
              className="gap-2 bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 shadow-lg"
            >
              <ScanLine className="h-5 w-5" />
              {isRTL ? 'مسح البطاقة' : 'Scan ID'}
            </Button>
          </div>
          
          <div className="mt-3 pt-3 border-t border-sky-200/50">
            <div className="flex items-center gap-2">
              <Input
                ref={barcodeInputRef}
                placeholder={isRTL ? 'أو أدخل رقم الباركود مباشرة...' : 'Or enter barcode directly...'}
                className="flex-1 bg-white/70"
                onFocus={() => setBarcodeMode(true)}
                onBlur={() => setBarcodeMode(false)}
                data-testid="barcode-input"
              />
              {barcodeMode && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full animate-pulse">
                  {isRTL ? 'جاهز للمسح' : 'Ready'}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Qatar ID Scanner Dialog - Main Patient */}
      <QatarIDScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanComplete={handleScanComplete}
        onLookupPatient={lookupPatient}
        isRTL={isRTL}
      />

      {/* Qatar ID Scanner Dialog - Family Member */}
      <QatarIDScanner
        isOpen={familyScannerOpen}
        onClose={() => { setFamilyScannerOpen(false); setScanningFamilyIndex(null); }}
        onScanComplete={handleFamilyScanComplete}
        onLookupPatient={lookupPatient}
        isRTL={isRTL}
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Patient Info */}
            <Card className="animate-fade-in stagger-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-sky-500" />
                  {isRTL ? 'بيانات المريض' : 'Patient Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_name" className="text-slate-700 font-medium">
                      {t('patient_name')} *
                    </Label>
                    <Input
                      id="patient_name"
                      value={formData.patient_name}
                      onChange={(e) => handleChange('patient_name', e.target.value)}
                      placeholder={isRTL ? 'أدخل اسم المريض' : 'Enter patient name'}
                      required
                      data-testid="patient-name-input"
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="patient_id_number" className="text-slate-700 font-medium">
                      {t('patient_id_number')} *
                    </Label>
                    <Input
                      id="patient_id_number"
                      value={formData.patient_id_number}
                      onChange={(e) => handleChange('patient_id_number', e.target.value)}
                      onBlur={(e) => checkPatientId(e.target.value)}
                      placeholder={isRTL ? 'أدخل رقم البطاقة الشخصية' : 'Enter ID card number'}
                      required
                      data-testid="patient-id-input"
                      className={`h-11 ${alreadyReceivedWarning ? 'border-red-500' : ''}`}
                    />
                    {checkingId && (
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <span className="h-3 w-3 border-2 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
                        {isRTL ? 'جاري التحقق...' : 'Checking...'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Patient Age - REQUIRED */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_age" className="text-slate-700 font-medium">
                      {t('patient_age')} *
                    </Label>
                    <Input
                      id="patient_age"
                      type="number"
                      min="0"
                      max="150"
                      value={formData.patient_age}
                      onChange={(e) => handleChange('patient_age', e.target.value)}
                      placeholder={isRTL ? 'أدخل عمر المريض' : 'Enter patient age'}
                      required
                      data-testid="patient-age-input"
                      className="h-11"
                    />
                    {formData.patient_age && parseInt(formData.patient_age) > 0 && parseInt(formData.patient_age) <= 150 && (
                      <p className="text-xs text-emerald-600">✓ {isRTL ? 'عمر صحيح' : 'Valid age'}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">{t('receipt_date')} *</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start h-11 font-normal" data-testid="receipt-date-button">
                          <CalendarIcon className="me-2 h-4 w-4 text-slate-500" />
                          {format(formData.receipt_date instanceof Date ? formData.receipt_date : new Date(formData.receipt_date), 'PPP', { locale: language === 'ar' ? ar : enUS })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.receipt_date instanceof Date ? formData.receipt_date : new Date(formData.receipt_date)}
                          onSelect={(date) => { if (date) { handleChange('receipt_date', date); setCalendarOpen(false); }}}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Already Received Warning */}
                {alreadyReceivedWarning && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 animate-fade-in">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-red-700">
                          {isRTL ? 'تحذير: هذا المريض استلم الدواء مسبقاً!' : 'Warning: Patient already received!'}
                        </p>
                        <p className="text-red-600">{isRTL ? 'التاريخ:' : 'Date:'} {alreadyReceivedWarning.receipt_date}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Receipt Type */}
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">{t('receipt_type')} *</Label>
                  <RadioGroup
                    value={formData.receipt_type}
                    onValueChange={(value) => handleChange('receipt_type', value)}
                    className="flex flex-col sm:flex-row gap-4"
                  >
                    <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.receipt_type === 'self' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-200'
                    }`}>
                      <RadioGroupItem value="self" id="self" data-testid="receipt-type-self" />
                      <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-sky-500" />
                        <span className="font-medium">{t('receipt_self')}</span>
                      </div>
                    </label>

                    <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.receipt_type === 'family' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-200'
                    }`}>
                      <RadioGroupItem value="family" id="family" data-testid="receipt-type-family" />
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-sky-500" />
                        <span className="font-medium">{t('receipt_family')}</span>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dose_mg" className="text-slate-700 font-medium">
                      {t('dose_mg')} {formData.receipt_status === 'received' && !formData.has_allergy && '*'}
                    </Label>
                    <Input
                      id="dose_mg"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.dose_mg}
                      onChange={(e) => handleChange('dose_mg', e.target.value)}
                      placeholder="0"
                      data-testid="dose-input"
                      className="h-11"
                      disabled={formData.has_allergy || formData.receipt_status !== 'received'}
                    />
                    {formData.has_allergy ? (
                      <p className="text-xs text-amber-600">
                        {isRTL ? 'لا يمكن إدخال جرعة للمريض بسبب الحساسية' : 'Cannot enter dose for patient due to allergy'}
                      </p>
                    ) : formData.receipt_status !== 'received' ? (
                      <p className="text-xs text-amber-600">
                        {isRTL ? 'الجرعة متاحة فقط عند حالة "تم الاستلام"' : 'Dose only available for "Received" status'}
                      </p>
                    ) : (
                      <p className="text-xs text-sky-600">
                        {isRTL ? 'أدخل عدد الحبات يدوياً' : 'Enter number of tablets manually'}
                      </p>
                    )}
                  </div>

                  {/* Receiving Area (replaces Dispensing Location) */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-sky-500" />
                      {t('receiving_area')} *
                    </Label>
                    <Select
                      value={formData.location_id}
                      onValueChange={(value) => handleChange('location_id', value)}
                    >
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder={isRTL ? 'اختر موقع التوزيع' : 'Select distribution location'} />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Family Members Section */}
            {formData.receipt_type === 'family' && (
              <Card className="animate-fade-in">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-sky-500" />
                    {t('family_members')}
                  </CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={addFamilyMember} className="gap-1">
                    <Plus className="h-4 w-4" />
                    {t('add_family_member')}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.family_members.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{isRTL ? 'اضغط لإضافة أفراد العائلة' : 'Click to add family members'}</p>
                    </div>
                  ) : (
                    formData.family_members.map((member, index) => (
                      <div key={index} className="p-4 rounded-lg bg-slate-50 border space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-700">
                            {isRTL ? `فرد العائلة ${index + 1}` : `Family Member ${index + 1}`}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFamilyMember(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Input
                            placeholder={t('patient_name')}
                            value={member.name}
                            onChange={(e) => updateFamilyMember(index, 'name', e.target.value)}
                          />
                          <div className="relative">
                            <Input
                              placeholder={t('patient_id_number')}
                              value={member.id_number}
                              onChange={(e) => updateFamilyMember(index, 'id_number', e.target.value)}
                              onBlur={(e) => checkFamilyDuplicateQID(index, e.target.value)}
                              className="pe-20"
                            />
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => openFamilyScanner(index)}
                              className="absolute top-1/2 -translate-y-1/2 end-1 h-7 px-2 gap-1 bg-sky-500 hover:bg-sky-600 text-white text-xs rounded"
                            >
                              <ScanLine className="h-3 w-3" />
                              {isRTL ? 'مسح' : 'Scan'}
                            </Button>
                          </div>
                          <Input
                            type="number"
                            placeholder={t('age')}
                            value={member.age}
                            onChange={(e) => updateFamilyMember(index, 'age', e.target.value)}
                            min="0"
                            max="150"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Input
                              type="number"
                              placeholder={t('dose_mg')}
                              value={member.dose_mg}
                              onChange={(e) => updateFamilyMember(index, 'dose_mg', e.target.value)}
                              disabled={member.has_allergy || formData.receipt_status !== 'received'}
                            />
                            {member.has_allergy ? (
                              <p className="text-xs text-amber-600">{isRTL ? 'حساسية - لا جرعة' : 'Allergy - no dose'}</p>
                            ) : formData.receipt_status === 'received' ? (
                              <p className="text-xs text-sky-600">
                                {isRTL ? 'أدخل عدد الحبات يدوياً' : 'Enter tablets manually'}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={member.has_allergy}
                              onCheckedChange={(checked) => updateFamilyMember(index, 'has_allergy', checked)}
                            />
                            <Label className="text-sm">{t('has_allergy_yes')}</Label>
                          </div>
                        </div>
                        {member.has_allergy && (
                          <Input
                            placeholder={t('allergy_notes')}
                            value={member.allergy_notes}
                            onChange={(e) => updateFamilyMember(index, 'allergy_notes', e.target.value)}
                            className="border-amber-300"
                          />
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            )}

            {/* Allergy & Medical Info */}
            <Card className="animate-fade-in stagger-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-amber-500" />
                  {t('has_allergy')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={formData.has_allergy ? 'yes' : 'no'}
                  onValueChange={(value) => handleChange('has_allergy', value === 'yes')}
                  className="flex gap-4"
                >
                  <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    !formData.has_allergy ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="no" id="no-allergy" />
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      <span className="font-medium text-emerald-700">{t('no_allergy')}</span>
                    </div>
                  </label>

                  <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.has_allergy ? 'border-red-500 bg-red-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="yes" id="has-allergy" />
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <span className="font-medium text-red-700">{t('has_allergy_yes')}</span>
                    </div>
                  </label>
                </RadioGroup>

                {formData.has_allergy && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200 animate-fade-in">
                    <div className="flex items-start gap-2 mb-3">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <p className="font-semibold text-red-700">{t('allergy_warning')}</p>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-red-700">{t('allergy_notes')} *</Label>
                        <Input
                          value={formData.allergy_notes}
                          onChange={(e) => handleChange('allergy_notes', e.target.value)}
                          placeholder={isRTL ? 'نوع الحساسية' : 'Type of allergy'}
                          className="border-red-300"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />
                    {t('medical_notes')} {formData.has_allergy && '*'}
                  </Label>
                  <Textarea
                    value={formData.medical_notes}
                    onChange={(e) => handleChange('medical_notes', e.target.value)}
                    placeholder={isRTL ? 'أدخل الملاحظات الطبية' : 'Enter medical notes'}
                    rows={3}
                    data-testid="medical-notes-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Receipt Details (Status only - removed signature & notify) */}
            <Card className="animate-fade-in stagger-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenLine className="h-5 w-5 text-sky-500" />
                  {isRTL ? 'تفاصيل الاستلام' : 'Receipt Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">{t('receipt_status')} *</Label>
                  <RadioGroup
                    value={formData.receipt_status}
                    onValueChange={(value) => {
                      if (formData.has_allergy && value === 'received' && formData.receipt_type === 'self') {
                        toast.error(t('allergy_warning'));
                        return;
                      }
                      handleChange('receipt_status', value);
                    }}
                    className="flex flex-col gap-3"
                  >
                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.receipt_status === 'received' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'
                    } ${formData.has_allergy && formData.receipt_type === 'self' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <RadioGroupItem value="received" id="received" disabled={formData.has_allergy && formData.receipt_type === 'self'} data-testid="status-received-radio" />
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        <span className="font-medium text-emerald-700">{t('received')}</span>
                      </div>
                      <span className="text-xs text-emerald-600 ms-auto">{isRTL ? 'يتم خصم الكمية من المخزون' : 'Stock will be deducted'}</span>
                    </label>

                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.receipt_status === 'not_received' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-200'
                    }`}>
                      <RadioGroupItem value="not_received" id="not_received" data-testid="status-not-received-radio" />
                      <div className="flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-red-500" />
                        <span className="font-medium text-red-700">{t('not_received')}</span>
                      </div>
                    </label>

                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.receipt_status === 'preparing' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-200'
                    }`}>
                      <RadioGroupItem value="preparing" id="preparing" data-testid="status-preparing-radio" />
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        <span className="font-medium text-amber-700">{t('preparing')}</span>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <Card className="animate-fade-in stagger-5">
                <CardHeader>
                  <CardTitle className="text-base">{isRTL ? 'ملخص التسجيل' : 'Registration Summary'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('patient_name')}</span>
                      <span className="font-medium">{formData.patient_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('patient_id_number')}</span>
                      <span className="font-medium font-mono">{formData.patient_id_number || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('patient_age')}</span>
                      <span className="font-medium">{formData.patient_age || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('receipt_date')}</span>
                      <span className="font-medium">{format(formData.receipt_date instanceof Date ? formData.receipt_date : new Date(formData.receipt_date), 'yyyy-MM-dd')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('receiving_area')}</span>
                      <span className="font-medium">{locations.find(l => l.id === formData.location_id)?.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('dose_mg')}</span>
                      <span className="font-medium">{formData.dose_mg || '0'} {isRTL ? 'حبة' : 'Tablet'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">{t('receipt_status')}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        formData.receipt_status === 'received' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : formData.receipt_status === 'preparing'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {formData.receipt_status === 'received' ? t('received') : formData.receipt_status === 'preparing' ? t('preparing') : t('not_received')}
                      </span>
                    </div>
                    {formData.has_allergy && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">{t('has_allergy')}</span>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                          {t('has_allergy_yes')}
                        </span>
                      </div>
                    )}
                    {formData.receipt_type === 'family' && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">{t('family_members')}</span>
                        <span className="font-medium">{formData.family_members.length}</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <Button
                      type="submit"
                      className={`w-full h-11 gap-2 btn-active ${
                        alreadyReceivedWarning || (formData.has_allergy && formData.receipt_status === 'received')
                          ? 'bg-slate-400 cursor-not-allowed' 
                          : 'bg-sky-500 hover:bg-sky-600'
                      }`}
                      disabled={isSubmitting || alreadyReceivedWarning}
                      data-testid="submit-receipt-button"
                    >
                      {isSubmitting ? (
                        <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          {t('submit_receipt')}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="text-xs text-slate-400 text-center">
                {t('permissions_note')}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewReceiptPage;
